import React, { useState } from "react";
import "./ForgotPassword.css";
import { useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import { apiUrl, otpApiUrl } from "../../config/api.js";
import { devLog } from "../../config/devLog.js";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const navigate = useNavigate();
  const [isVerified, setIsVerified] = useState(false);
  /** Short-lived JWT from verifyForgotPasswordOTP; required for changePassword. */
  const [resetToken, setResetToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetError, setResetError] = useState("");

  const handleSendOtp = (e) => {
    e.preventDefault();
    devLog("[forgot-password] request OTP");
    getOTP();
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    devLog("[forgot-password] verify OTP submitted");
    setResetError("");
    try {
      const response = await fetch(apiUrl("verifyForgotPasswordOTP"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.verified && data.resetToken) {
        setResetToken(data.resetToken);
        setIsVerified(true);
      } else {
        setResetError(
          data.message ||
            (data.verified && !data.resetToken
              ? "Verification succeeded but reset session is missing. Try again."
              : "Verification failed.")
        );
      }
      devLog("[forgot-password] verify response", {
        ok: response.ok,
        verified: data?.verified,
      });
    } catch (err) {
      devLog("[forgot-password] verify error", err?.message);
      setResetError("Could not verify OTP. Try again.");
    }
  };

  const getOTP = async () => {
    setLoading(true);
    setResetError("");
    try {
      const response = await fetch(otpApiUrl("getOTP"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      await response.json();
      devLog("[forgot-password] getOTP response", { ok: response.ok });
      if (response.ok) {
        setIsOtpSent(true);
      } else {
        setResetError("Could not send OTP.");
      }
    } catch (err) {
      devLog("[forgot-password] getOTP error", err?.message);
      setResetError("Could not send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError("");
    devLog("[forgot-password] reset password submit");
    if (!resetToken) {
      setResetError("Session expired. Verify the OTP again.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(apiUrl("changePassword"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password: newPassword,
          resetToken,
        }),
      });
      const data = await response.json().catch(() => ({}));
      devLog("[forgot-password] changePassword response", { ok: response.ok });
      if (response.ok) {
        navigate("/");
      } else {
        setResetError(data.message || "Password reset failed. Try again.");
      }
    } catch (error) {
      devLog("Error resetting password:", error?.message);
      setResetError("Password reset failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-box">
        {loading ? (
          <>
            <FaSpinner className="spinner" />
            Processing...
          </>
        ) : null}
        <h3 style={{ fontWeight: "normal", margin: "10px auto" }}>
          Forgot Password
        </h3>
        {resetError ? (
          <p style={{ color: "#c0392b", fontSize: "14px" }}>{resetError}</p>
        ) : null}
        {!isOtpSent ? (
          <form onSubmit={handleSendOtp}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ textAlign: "left", fontWeight: "bold" }}>
                Email
              </label>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: "90%",
                  padding: "5px",
                  border: "1px solid lightgrey",
                  borderRadius: "5px",
                  fontSize: "16px",
                  outline: "none",
                }}
              />
            </div>
            <button type="submit" className="send-otp-button">
              Send OTP
            </button>
          </form>
        ) : !isVerified ? (
          <form onSubmit={handleVerifyOtp}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ textAlign: "left", fontWeight: "bold" }}>
                OTP
              </label>
              <input
                type="text"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                style={{
                  width: "90%",
                  padding: "5px",
                  border: "1px solid lightgrey",
                  borderRadius: "5px",
                  fontSize: "16px",
                  outline: "none",
                }}
              />
            </div>
            <button type="submit" className="verify-otp-button">
              Verify
            </button>
          </form>
        ) : (
          <form onSubmit={handleResetPassword}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                margin: "10px 0px 20px 0px",
              }}
            >
              <label style={{ textAlign: "left", fontWeight: "bold" }}>
                New Password
              </label>
              <input
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                style={{
                  width: "90%",
                  padding: "5px",
                  border: "1px solid lightgrey",
                  borderRadius: "5px",
                  fontSize: "16px",
                  outline: "none",
                }}
              />
            </div>
            <button type="submit" className="reset-password-button">
              Reset Password
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
